import { SyncMessage } from '@teamworksdev/loco-sync-client';
import { MS } from '@strength/loco-sync';

const PING_INTERVAL = 30 * 1000;
const PONG_TIMEOUT = 60 * 1000;

export const retryDelay = (attemptIndex: number) =>
  attemptIndex === 0 ? 0 : Math.min(1000 * 2 ** attemptIndex, 30000);

export interface WebSocketClientOptions {
  retryDelayMs?: typeof retryDelay;
  onOpen?: (socket: WebSocket) => void;
  onClose?: (cause?: { code: number }) => void;
}

type WebSocketMessage =
  | SyncMessage<MS>
  | {
      type: 'auth_error';
    }
  | {
      type: 'error';
      message: string;
    }
  | {
      type: 'pong';
    };

export function createWebSocketClient(
  url: string,
  callback: (msg: WebSocketMessage) => void,
  opts?: WebSocketClientOptions,
) {
  const retryDelayFn = opts?.retryDelayMs ?? retryDelay;
  const onClose = opts?.onClose;
  let connectAttempt = 0;
  let connectTimer: NodeJS.Timer | number | null = null;
  let state: 'closed' | 'connecting' | 'open' = 'connecting';

  let pingInterval: NodeJS.Timer | number | null = null;
  let lastPong = Date.now();

  let activeConnection = createConnection();

  function reconnect() {
    state = 'connecting';
    activeConnection = createConnection();
  }

  function reconnectInMs(ms: number) {
    if (connectTimer) {
      return;
    }
    state = 'connecting';
    connectTimer = setTimeout(reconnect, ms);
  }

  function tryReconnect() {
    if (connectTimer !== null || state === 'closed') {
      return;
    }
    const timeout = retryDelayFn(connectAttempt++);
    reconnectInMs(timeout);
  }

  function createConnection() {
    const conn = new WebSocket(url);

    clearTimeout(connectTimer as any);
    clearInterval(pingInterval as any);

    connectTimer = null;
    pingInterval = null;

    conn.addEventListener('open', () => {
      if (conn !== activeConnection) {
        return;
      }

      opts?.onOpen?.(conn);

      connectAttempt = 0;
      state = 'open';

      // Send a message to the server every 30 seconds to make sure our connection
      // is still alive; if not, re-establish it
      lastPong = Date.now();
      pingInterval = setInterval(() => {
        if (Date.now() - lastPong > PONG_TIMEOUT) {
          console.warn(
            'Websocket keep alive timed out. Re-establishing connection...',
          );
          clearInterval(pingInterval as any);
          pingInterval = null;
          connectAttempt = 0;
          conn.close();
          return;
        }

        if (conn.readyState !== WebSocket.OPEN) return;
        conn.send(JSON.stringify({ type: 'ping' }));
      }, PING_INTERVAL);
    });

    conn.addEventListener('error', () => {
      if (conn === activeConnection) {
        tryReconnect();
      }
    });

    conn.addEventListener('message', ({ data }) => {
      const msg = JSON.parse(data) as WebSocketMessage;
      // Any message shows that we are still connected, so we can reset the timer
      lastPong = Date.now();

      if (msg.type !== 'pong') {
        callback(msg);
      }
    });

    conn.addEventListener('close', (data) => {
      if (state === 'open') {
        onClose?.(data);
      }

      if (activeConnection === conn) {
        clearInterval(pingInterval as any);
        pingInterval = null;

        // connection might have been replaced already
        tryReconnect();
      }
    });

    return conn;
  }

  return () => {
    state = 'closed';
    onClose?.();
    activeConnection.close();
    clearTimeout(connectTimer as any);
    clearInterval(pingInterval as any);
    connectTimer = null;
    pingInterval = null;
  };
}
